import { useMemo } from 'react';
import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import WizardMultiSelectChips from '../../../components/ui/WizardMultiSelectChips';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useCaseRequestWizard } from '../../../hooks/useCaseRequestWizard';
import { FieldError } from './ExtraditionMlaWizardShell';
import { useCaseCategorization } from './useCaseCategorization';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

type Props = {
  draft: ExtraditionMlaWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<ExtraditionMlaWizardDraft>>;
  editable?: boolean;
  errors?: RequiredErrors;
  onClearError?: (key: string) => void;
};

function SelectField({
  id,
  label,
  required,
  value,
  disabled,
  hasError,
  placeholder,
  options,
  onChange,
  onClear,
}: {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  disabled?: boolean;
  hasError?: boolean;
  placeholder: string;
  options: { id: string; name: string }[];
  onChange: (id: string) => void;
  onClear?: () => void;
}) {
  return (
    <div className="ccr-fg">
      <label>
        {label} {required ? <span className="req">*</span> : null}
      </label>
      <WizardSelect
        id={id}
        value={value}
        disabled={disabled}
        hasError={hasError}
        placeholder={placeholder}
        options={options.map((o) => ({ id: o.id, label: o.name }))}
        onChange={onChange}
        onClear={onClear}
      />
    </div>
  );
}

export function ExtraditionMlaCaseCategorizationCard({
  draft,
  setDraft,
  editable = false,
  errors = {},
  onClearError,
}: Props) {
  const f = useWizardFieldLabels();
  const { switchFileCategory } = useCaseRequestWizard();
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId, {
    enabled: editable || Boolean(draft.fileCategoryId),
  });

  const fileLabel = cat.fileCategoryName || draft.fileCategory || '—';
  const subLabel = cat.subFileCategoryName || draft.subFileCategoryName || '—';

  const fileCategoryOptions = useMemo(() => {
    const byId = new Map(cat.fileCategories.map((c) => [c.id, c]));
    if (draft.fileCategoryId && !byId.has(draft.fileCategoryId)) {
      byId.set(draft.fileCategoryId, {
        id: draft.fileCategoryId,
        name: cat.fileCategoryName || draft.fileCategory || draft.fileCategoryId,
      });
    }
    return Array.from(byId.values());
  }, [cat.fileCategories, cat.fileCategoryName, draft.fileCategory, draft.fileCategoryId]);

  const subCategoryOptions = useMemo(() => {
    const byId = new Map(cat.subCategories.map((s) => [s.id, s]));
    if (draft.subFileCategoryId && !byId.has(draft.subFileCategoryId)) {
      byId.set(draft.subFileCategoryId, {
        id: draft.subFileCategoryId,
        name: cat.subFileCategoryName || draft.subFileCategoryName || draft.subFileCategoryId,
      });
    }
    return Array.from(byId.values());
  }, [cat.subCategories, cat.subFileCategoryName, draft.subFileCategoryId, draft.subFileCategoryName]);

  const areaLabels =
    draft.applicableAreas.length > 0
      ? draft.applicableAreas
      : cat.resolveAreaLabels(draft.applicableAreaIds);

  const applicableAreaOptions = useMemo(() => {
    const byId = new Map(cat.applicableAreaOptions.map((o) => [o.id, o]));
    draft.applicableAreaIds.forEach((id, index) => {
      if (!id || byId.has(id)) return;
      const label = draft.applicableAreas[index]?.trim() || id;
      byId.set(id, { id, label });
    });
    return Array.from(byId.values());
  }, [cat.applicableAreaOptions, draft.applicableAreaIds, draft.applicableAreas]);

  const onFileCategoryChange = (id: string) => {
    if (!setDraft) return;
    const row = cat.fileCategories.find((c) => c.id === id);
    const name = row?.name ?? '';
    switchFileCategory({
      fromFlow: 'extradition-mla',
      categoryId: id,
      categoryName: name,
      categoryCode: row?.code,
    });
    onClearError?.('fileCategoryId');
    onClearError?.('subFileCategoryId');
    onClearError?.('applicableAreas');
  };

  const onSubCategoryChange = (id: string) => {
    if (!setDraft || !draft.fileCategoryId) return;
    const name = cat.subCategories.find((s) => s.id === id)?.name ?? '';
    setDraft((d) => ({
      ...d,
      subFileCategoryId: id,
      subFileCategoryName: name,
      applicableAreaIds: [],
      applicableAreas: [],
      documents: [],
    }));
    onClearError?.('subFileCategoryId');
    onClearError?.('applicableAreas');
  };

  const onAreasChange = (ids: string[]) => {
    if (!setDraft) return;
    setDraft((d) => ({
      ...d,
      applicableAreaIds: ids,
      applicableAreas: cat.resolveAreaLabels(ids),
    }));
    onClearError?.('applicableAreas');
  };

  const fileCategorySelected =
    Boolean(draft.fileCategoryId) &&
    !cat.loadingCategories &&
    cat.fileCategories.some((c) => c.id === draft.fileCategoryId);

  const subCategorySelected =
    Boolean(draft.subFileCategoryId) &&
    (!cat.loadingSubCategories ||
      subCategoryOptions.some((s) => s.id === draft.subFileCategoryId));

  const subDisabled = !fileCategorySelected || cat.loadingSubCategories;
  const areasDisabled = !fileCategorySelected || !subCategorySelected || cat.loadingAreas;

  return (
    <div className="ccr-cat-card">
      <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
        {editable && setDraft ? (
          <div>
            <SelectField
              id="emla-fileCategory"
              label={f.fileCategory}
              required
              value={draft.fileCategoryId}
              disabled={cat.loadingCategories}
              hasError={Boolean(errors.fileCategoryId)}
              placeholder={f.selectFileCategory}
              options={fileCategoryOptions}
              onChange={onFileCategoryChange}
              onClear={() => onFileCategoryChange('')}
            />
            <FieldError message={errors.fileCategoryId} />
          </div>
        ) : (
          <div className="ccr-fg">
            <label>
              {f.fileCategory} <span className="req">*</span>
            </label>
            <input className="ccr-inp" value={fileLabel} readOnly disabled />
          </div>
        )}
        {editable && setDraft ? (
          <div>
            <SelectField
              id="emla-subFileCategory"
              label={f.subFileCategory}
              required
              value={draft.subFileCategoryId}
              disabled={subDisabled}
              hasError={Boolean(errors.subFileCategoryId)}
              placeholder={f.selectSubFileCategory}
              options={subCategoryOptions}
              onChange={onSubCategoryChange}
              onClear={() => onSubCategoryChange('')}
            />
            <FieldError message={errors.subFileCategoryId} />
          </div>
        ) : (
          <div className="ccr-fg">
            <label>
              {f.subFileCategory} <span className="req">*</span>
            </label>
            <input className="ccr-inp" value={subLabel} readOnly disabled />
          </div>
        )}
      </div>

      <div className="ccr-fg" id="emla-applicableAreas" style={{ marginBottom: 14 }}>
        <label>{f.applicableAreas}</label>
        {editable && setDraft ? (
          <>
            <WizardMultiSelectChips
              id="emla-applicableAreas-input"
              options={applicableAreaOptions}
              value={draft.applicableAreaIds}
              onChange={onAreasChange}
              disabled={areasDisabled}
              hasError={Boolean(errors.applicableAreas)}
              placeholder={f.selectApplicableAreas}
              aria-label={f.applicableAreas}
            />
            <FieldError message={errors.applicableAreas} />
          </>
        ) : (
          <div className="ccr-area-tags read-only">
            {areaLabels.map((area) => (
              <span key={area} className="ccr-area-tag">
                {area}
              </span>
            ))}
            {areaLabels.length === 0 ? <span style={{ fontSize: 12, color: 'var(--t3)' }}>—</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}
